import { IGifService, IGifServiceProps } from '@src/slide/application/service/gif-service/index.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IReactionDisposer, reaction } from 'mobx';
import ImageQuickLookManager
    from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';

export const MAX_RETRY_TIME = 2;
export const IMAGE_CACHE_TIMEOUT = 1000 * 10;

export const MIME_TYPE_GIF = 'image/gif';
export const MIME_TYPE_WEBP = 'image/webp';

export class GifService implements IGifService {
    /**
     * 图片资源缓存，考虑到内存和秒看的原因，先关闭
     */
    public gifCache = new Map<string, {
        imageArrayBufferPromise?: Promise<ArrayBuffer>;
    }>();

    public openGifCache = true;

    private reaction: IReactionDisposer;

    private services: IGifServiceProps;

    public constructor() {
    }

    public get isPlayerPlaying() {
        return this.services.playManager.isPlaying;
    }

    public get currentPlayingSlideModel(): ISlideModel | undefined {
        return this.services.playManager.currentSlideModel;
    }

    public setDelegate = (gifServiceProps: IGifServiceProps) => {
        this.services = gifServiceProps;
    };

    /**
     * 根据秒看的缓存获取gif blob进行解码
     * @param gifUrl
     */
    public getGifDecoderByCache = async (gifUrl: string): Promise<ImageDecoder> => {
        const quickCatch = ImageQuickLookManager.getInstance().getResourceByUrl(gifUrl);
        const blob = quickCatch?.getBlob();
        if (blob) {
            const fr = new FileReader();
            fr.readAsArrayBuffer(blob);
            const promise = new Promise((resolve: (value: Promise<ImageDecoder>) => void, reject) => {
                fr.addEventListener('loadend', (e) => {
                    const arrayBuffer = e.target?.result;
                    if (arrayBuffer instanceof ArrayBuffer) {
                        resolve(this.creatAndAwaitDecoder(arrayBuffer, blob.type));
                        return;
                    }

                    reject();
                });
            });

            return promise;
        }

        // 兜底
        return this.getImageDecoderByUrl(gifUrl);
    };

    /**
     * 如果拿不到秒看的缓存，走fetch url流程
     * 通过gif url fetch下来gif图，缓存文件buffer，然后解码，
     * 还有一种情况，秒看缓存在转buffer或者解码时被catch了，走这里兜底
     * @param gifUrl
     */
    public getImageDecoderByUrl = async (gifUrl: string) => {
        const quickCatch = ImageQuickLookManager.getInstance().getResourceByUrl(gifUrl);
        const originUrl = quickCatch?.getCdnUrl() || gifUrl;

        let gifCache = this.gifCache.get(originUrl);
        let imageArrayBuffer: ArrayBuffer;
        if (!gifCache?.imageArrayBufferPromise) {
            const gifResourcePromise = this.requestGifResourceWithRetry(originUrl, 0);
            gifCache = {
                imageArrayBufferPromise: gifResourcePromise,
            };

            if (this.openGifCache) {
                this.gifCache.set(originUrl, gifCache);
            }

            imageArrayBuffer = await gifResourcePromise;
            gifResourcePromise.finally(() => {
                // gif图片比较大，缓存只在有效期内保留
                setTimeout(() => {
                    this.gifCache.delete(originUrl);
                }, IMAGE_CACHE_TIMEOUT);
            });
        } else {
            imageArrayBuffer = await gifCache.imageArrayBufferPromise;
        }

        return this.getImageDecoder(originUrl, imageArrayBuffer);
    };

    public deleteGifDecoderByCache = (gifUrl: string) => {
        this.gifCache.delete(gifUrl);
    };

    /**
     * 给加载gif包一层重试
     * @param url
     * @param retryTime
     */
    public requestGifResourceWithRetry = (
        url: string,
        retryTime = 0,
    ): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
        const requestPromise = this.loadGifResource(url);
        requestPromise
            .then(image => resolve(image))
            .catch(() => {
                retryTime += 1;
                if (retryTime <= MAX_RETRY_TIME) {
                    this.requestGifResourceWithRetry(url, retryTime)
                        .then(image => resolve(image))
                        .catch(() => reject());
                } else {
                    reject();
                }
            });
    });

    /**
     * gif资源加载，将buffer缓存下来
     * @param url
     * @private
     */
    public loadGifResource = async (url: string): Promise<ArrayBuffer> => {
        const response = await fetch(url);
        return response.arrayBuffer();
    };

    /**
     * 根据文件流与图片类型获取解码器实例，目前这块有个坑，就是cdn对gif的格式转换问题，最好能在数据测告知当前动图的类型
     * @param url
     * @param imageByteStream
     */
    private getImageDecoder = async (url: string, imageByteStream: ArrayBuffer): Promise<ImageDecoder> => {
        let type = MIME_TYPE_GIF;
        if (url.includes('.webp') && !url.includes('gif')) {
            type = MIME_TYPE_WEBP;
        }

        try {
            return await this.creatAndAwaitDecoder(imageByteStream, type);
        } catch (e) {
            // todo: 目前看起来，cdn测在上传图片时有不同逻辑，有的会将gif转成webp，有时又是gif，看看cdn能不能统一
            if (type === MIME_TYPE_GIF) {
                type = MIME_TYPE_WEBP;
            } else {
                type = MIME_TYPE_GIF;
            }

            return this.creatAndAwaitDecoder(imageByteStream, type);
        }
    };

    /**
     * 创建解码器
     * @param imageByteStream
     * @param type
     * @private
     */
    private async creatAndAwaitDecoder(imageByteStream: ArrayBuffer, type: string): Promise<ImageDecoder> {
        const imageDecoder = new window.ImageDecoder({
            data: imageByteStream,
            type,
        });

        // 等待 imageDecoder 初始化完成
        await imageDecoder.tracks.ready;
        await imageDecoder.completed;
        return imageDecoder;
    }
}
